export enum AssetScope {
  GLOBAL = "GLOBAL",
  ORG = "ORG",
}

export enum AssetStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  SCAN_FAILED = "SCAN_FAILED",
  ARCHIVED = "ARCHIVED",
}

export enum AssetType {
  PDF = "PDF",
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  DOCUMENT = "DOCUMENT",
  MARKDOWN = "MARKDOWN",
  OTHER = "OTHER",
}

export interface Asset {
  assetId: string;
  scope: string;
  orgId: string;
  status: string;
  assetType: AssetType;
  title: string;
  description?: string;
  altText?: string;
  tags?: string[];
  contentType: string;
  fileKey?: string;
  fileSizeBytes?: number;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  archivedAt?: string;
  archivedBy?: string;
}

export interface ListAssetsInput {
  scope: AssetScope;
  orgId?: string;
  assetType?: string;
  query: string;
  status?: string;
  limit?: number;
  nextToken?: string;
}

export interface ListAssetsResponse {
  listAssets: {
    items: Asset[];
    nextToken?: string;
  };
}

export interface GetAssetResponse {
  getAsset: Asset;
}

export interface GetAssetDownloadUrlInput {
  assetId: string;
  orgId?: string;
}

export interface GetAssetDownloadUrlResponse {
  getAssetDownloadUrl: {
    asset: Pick<Asset, "assetId" | "title" | "status">;
    downloadUrl: string;
    expiresAt: string;
  };
}

export interface CreateAssetInput {
  scope: AssetScope;
  orgId?: string;
  assetType: string;
  title: string;
  description?: string;
  contentType: string;
}

export interface CreateAssetResponse {
  createAsset: {
    asset: Asset;
    uploadUrl: string;
    uploadKey: string;
    uploadExpiresAt: string;
    uploadHeaders: string | null;
  };
}

export interface ArchiveAssetInput {
  assetId: string;
  orgId?: string;
}

export interface ArchiveAssetResponse {
  archiveAsset: {
    assetId: string;
    status: string;
    archivedAt: string;
    archivedBy: string;
  };
}

export type AssetUploadStatus =
  | "idle"
  | "creating"
  | "subscribing"
  | "uploading"
  | "scanning"
  | "active"
  | "failed";

export interface AssetEvent {
  assetId: string;
  scope: AssetScope;
  orgId?: string;
  assetType: string;
  oldStatus: AssetStatus;
  newStatus: AssetStatus;
  title: string;
  updatedAt: string;
}

export interface CopyAssetToOrgInput {
  assetId: string;
  orgId: string;
}

export interface CopyAssetToOrgResponse {
  copyAssetToOrg: Pick<
    Asset,
    | "assetId"
    | "scope"
    | "orgId"
    | "status"
    | "assetType"
    | "title"
    | "description"
    | "contentType"
    | "fileKey"
    | "fileSizeBytes"
    | "createdBy"
    | "createdAt"
  >;
}
// ---- Assets Domain Queries & Mutations ----

export const CREATE_ASSET = `
  mutation CreateAsset($input: CreateAssetInput!) {
    createAsset(input: $input) {
      asset {
        assetId
        scope
        orgId
        status
        assetType
        title
        description
        contentType
        createdBy
        createdAt
      }
      uploadUrl
      uploadKey
      uploadExpiresAt
      uploadHeaders
    }
  }
`;

export const LIST_ASSETS = `
  query ListAssets($input: ListAssetsInput!) {
    listAssets(input: $input) {
      items {
        assetId
        scope
        orgId
        status
        assetType
        title
        description
        contentType
        fileKey
        fileSizeBytes
        createdBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_ASSET = `
  query GetAsset($assetId: String!, $orgId: String) {
    getAsset(assetId: $assetId, orgId: $orgId) {
      assetId
      scope
      orgId
      status
      assetType
      title
      description
      altText
      tags
      contentType
      fileKey
      fileSizeBytes
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const GET_DOWNLOAD_URL = `
  query GetAssetDownloadUrl($input: GetAssetDownloadUrlInput!) {
    getAssetDownloadUrl(input: $input) {
      asset {
        assetId
        title
        status
      }
      downloadUrl
      expiresAt
    }
  }
`;

export const ARCHIVE_ASSET = `
  mutation ArchiveAsset($input: ArchiveAssetInput!) {
    archiveAsset(input: $input) {
      assetId
      status
      archivedAt
      archivedBy
    }
  }
`;

export const COPY_ASSET_TO_ORG = `
  mutation CopyAssetToOrg($input: CopyAssetToOrgInput!) {
    copyAssetToOrg(input: $input) {
      assetId
      scope
      orgId
      status
      assetType
      title
      description
      contentType
      fileKey
      fileSizeBytes
      createdBy
      createdAt
    }
  }
`;

// ---- Assets Domain Subscription ----

export const ON_ASSET_EVENT = `
  subscription OnAssetEvent($assetId: String!) {
    onAssetEvent(assetId: $assetId) {
      assetId
      scope
      orgId
      assetType
      oldStatus
      newStatus
      title
      updatedAt
    }
  }
`;
